import { useMemo, useState, type ReactNode } from 'react';
import { hucreMetni, kartRolu, sayisalMi, type HucreDegeri, type KartRolu, type RaporKolonu } from './kolon';
import type { GorunumProps } from './gorunum';
import { KartSirala, type SiralaSecenegi } from './KartSirala';

/**
 * S334: `RaporTablosu`nun TELEFON kipi. Tablo yerine kart listesi; kurallar Yerlice panelinin telefon
 * çalışmasından (kanıtlar parantezde, `src/RaporGor.CustomerPanel.Yerlice/src` altında):
 *
 *  - Liste tablo değil KARTTIR; kart satırın bütün kolonlarını alt alta dizmez, başlık + birkaç ölçü
 *    taşır (`components/DuzGezgin.tsx` başlık notu: her kolonu basan kart 217-276 px tutuyordu).
 *  - Kartın tamamı dokunma hedefidir, tek bir düğme (`styles/mobil.css` `.agac-kart`).
 *  - Kırpılabilen tek alan başlıktır, o da iki satıra kadar sarar; para kırpılmaz
 *    (`mobil.css` `.agac-kart__ad`, `styles/dashboard.css` `.belge-kalem__ad`).
 *  - Sanallaştırma yok: ilk 50 kart basılır, kalanı "+N kalem daha göster" açar ve sayı düğmede yazar
 *    (`components/AgacGezgin.tsx` `SAYFA_BOYU`). Toplam yine TÜM satırlardan.
 *  - Sıralama "Sırala" düğmesi + alttan sayfa; seçenekler kartın gösterdiği alanlar (`KartSirala.tsx`).
 *  - Kendi kaydırıcısı yoktur: liste doğal boyunda uzar, sayfa (ya da tam ekran pencere) kayar; TEK
 *    kaydırma (`mobil.css` S127 notu, `dashboard.css` S300 bloğu).
 *  - Toplam altta sabit şerittir (`components/BelgeDetayModal.tsx` `fatura-pop__genel-serit`).
 *
 * Kolonun karttaki yeri `RaporKolonu.kart` ile verilir (bkz. `kolon.KartRolu`).
 */
const SAYFA_BOYU = 50;

interface KartKolonu<S> {
  kolon: RaporKolonu<S>;
  /** `kolonlar` içindeki sırası (toplam ve ara toplam dizileri bu sırayla). */
  sira: number;
}
type Yerlesim<S> = Record<Exclude<KartRolu, 'gizli'>, KartKolonu<S>[]>;

function yerlesimKur<S>(kolonlar: readonly RaporKolonu<S>[]): Yerlesim<S> {
  const y: Yerlesim<S> = { baslik: [], olcu: [], olcuAlt: [], ek: [], alt: [], ikincil: [] };
  kolonlar.forEach((kolon, sira) => {
    const rol = kartRolu(kolon, sira);
    if (rol !== 'gizli') y[rol].push({ kolon, sira });
  });
  return y;
}

/** Kartın bir alanı: metni boşsa null (boş alan kartta yer tutmaz). */
function alan<S>(k: KartKolonu<S>, v: HucreDegeri, etiketli: boolean): ReactNode {
  const metin = hucreMetni(k.kolon.tur, v);
  if (metin === '') return null;
  const etiket = k.kolon.kartEtiketi ?? (etiketli ? k.kolon.baslik : undefined);
  return (
    <span key={k.kolon.id} className={'rk-alan' + (sayisalMi(k.kolon.tur) ? ' rk-sayi' : '')}>
      {etiket && <span className="rk-etiket">{etiket}</span>}
      {k.kolon.hucre ? k.kolon.hucre(v) : metin}
    </span>
  );
}

/** Kartın (ya da toplam şeridinin) gövdesi: değerler `deger(kolon)` ile okunur. */
function KartGovdesi<S>({ yerlesim: y, deger, baslik }: {
  yerlesim: Yerlesim<S>;
  deger: (k: KartKolonu<S>) => HucreDegeri;
  /** Verilirse başlık kolonlarının yerine bu basılır (grup adı, toplam etiketi): ÖZET kartı. */
  baslik?: ReactNode;
}) {
  // Birden çok ana ölçü varsa (Borç / Alacak) hangisi olduğu yazmalı; tek ölçü etiketsizdir.
  const olcuEtiketli = y.olcu.length > 1;
  // ÖZET kartında (grup, toplam) çoklu ana ölçülerin HEPSİ doludur ve başlık satırına sığmaz: etiketli
  // alt satıra iner, başlığın sağına `olcuAlt` (Bakiye) çıkar. Satır kartında yalnız biri doludur.
  const yerlesim: Yerlesim<S> = baslik !== undefined && olcuEtiketli
    ? { ...y, olcu: y.olcuAlt, olcuAlt: [], ikincil: [...y.olcu, ...y.ikincil] }
    : y;
  const basliklar = baslik ?? yerlesim.baslik
    .map((k) => hucreMetni(k.kolon.tur, deger(k)))
    .filter((m) => m !== '')
    .join(' · ');
  const olculer = yerlesim.olcu.map((k) => alan(k, deger(k), olcuEtiketli || yerlesim !== y)).filter(Boolean);
  const ekler = yerlesim.ek.map((k) => alan(k, deger(k), false)).filter(Boolean);
  const altlar = yerlesim.alt.map((k) => alan(k, deger(k), false)).filter(Boolean);
  const olcuAltlar = yerlesim.olcuAlt.map((k) => alan(k, deger(k), true)).filter(Boolean);
  const ikinciller = yerlesim.ikincil.map((k) => alan(k, deger(k), true)).filter(Boolean);
  return (
    <>
      <span className="rk-ust">
        <span className="rk-baslik">{basliklar}</span>
        {(olculer.length > 0 || ekler.length > 0) && <span className="rk-olcu">{olculer}{ekler}</span>}
      </span>
      {(altlar.length > 0 || olcuAltlar.length > 0) && (
        <span className="rk-orta">
          <span className="rk-alt">{altlar}</span>
          {olcuAltlar.length > 0 && <span className="rk-olcu-alt">{olcuAltlar}</span>}
        </span>
      )}
      {ikinciller.length > 0 && <span className="rk-ikincil">{ikinciller}</span>}
    </>
  );
}

type Oge<S> =
  | { tur: 'ust'; satir: S; anahtar: string }
  | { tur: 'grup'; ad: string; sayi: number; acik: boolean }
  | { tur: 'satir'; satir: S; ic: boolean };

export function KartGorunumu<S>({
  kolonlar, sirali, ust, gruplar, grup, grupAcik, grubuCevir, araToplamlar, toplamlar, etiketMetni,
  siralama, siralaDegis, satirKimligi, onSatirTikla, seciliKimlik, bosMesaj, yukleniyor, yenileniyor,
  testid, etiket, className, kartAraclari, kartStili,
}: GorunumProps<S>) {
  const yerlesim = useMemo(() => yerlesimKur(kolonlar), [kolonlar]);

  // Sıralama seçenekleri: kartta GÖRÜNEN alanlar (işaret kolonu hariç). Metin A-Z, sayı ve tarih çoktan aza.
  const secenekler = useMemo<SiralaSecenegi[]>(
    () => [...yerlesim.baslik, ...yerlesim.olcu, ...yerlesim.olcuAlt, ...yerlesim.alt, ...yerlesim.ikincil].map((k) => ({
      id: k.kolon.id,
      etiket: k.kolon.baslik,
      varsayilanYon: sayisalMi(k.kolon.tur) || k.kolon.tur === 'tarih' ? -1 : 1,
    })),
    [yerlesim],
  );

  // Liste değişince (süzgeç, sıralama, gruplama) sayfa başa döner: önceki listenin açılmış kuyruğu taşınmaz.
  const [limit, setLimit] = useState(SAYFA_BOYU);
  const [oncekiListe, setOncekiListe] = useState<unknown>(sirali);
  if (oncekiListe !== sirali) {
    setOncekiListe(sirali);
    setLimit(SAYFA_BOYU);
  }

  // Yalnız ilk `limit` öğe kurulur: 192 bin satırda bütün listeyi sarmalayıcı nesnelere çevirmeyiz.
  let ogeSayisi = ust.length;
  const ogeler: Oge<S>[] = [];
  const ekle = (o: Oge<S>) => { if (ogeler.length < limit) ogeler.push(o); };
  ust.forEach((satir, i) => ekle({ tur: 'ust', satir, anahtar: `u:${i}` }));
  if (gruplar) {
    for (const g of gruplar) {
      const acik = grupAcik(g.ad);
      ogeSayisi += 1 + (acik ? g.satirlar.length : 0);
      ekle({ tur: 'grup', ad: g.ad, sayi: g.satirlar.length, acik });
      if (acik) for (let i = 0; i < g.satirlar.length && ogeler.length < limit; i++) ekle({ tur: 'satir', satir: g.satirlar[i], ic: true });
    }
  } else {
    ogeSayisi += sirali.length;
    for (let i = 0; i < sirali.length && ogeler.length < limit; i++) ekle({ tur: 'satir', satir: sirali[i], ic: false });
  }
  const kalan = ogeSayisi - ogeler.length;

  const kart = (o: Oge<S>): ReactNode => {
    if (o.tur === 'grup') {
      const ara = araToplamlar.get(o.ad) ?? [];
      return (
        <li key={`g:${o.ad}`}>
          <button
            type="button"
            className="rk-kart rk-kart--grup"
            data-testid={`${testid}-grup`}
            data-grup={o.ad}
            aria-expanded={o.acik}
            onClick={() => grubuCevir(o.ad)}
          >
            <KartGovdesi
              yerlesim={yerlesim}
              deger={(k) => (k.kolon.toplam ? ara[k.sira] : undefined)}
              baslik={
                <>
                  <span className="rk-grup-ok" aria-hidden="true">{o.acik ? '▾' : '▸'}</span>
                  {o.ad}
                  <span className="rk-grup-say">{o.sayi}{grup?.birim ? ` ${grup.birim}` : ''}</span>
                </>
              }
            />
          </button>
        </li>
      );
    }
    const satir = o.satir;
    const govde = <KartGovdesi yerlesim={yerlesim} deger={(k) => k.kolon.deger(satir)} />;
    if (o.tur === 'ust') {
      return <li key={o.anahtar}><div className="rk-kart rk-kart--ust" data-testid={`${testid}-kart`}>{govde}</div></li>;
    }
    const kimlik = satirKimligi(satir);
    const sinif = 'rk-kart' + (o.ic ? ' rk-kart--ic' : '');
    return (
      <li key={kimlik}>
        {onSatirTikla ? (
          <button
            type="button"
            className={sinif}
            data-testid={`${testid}-kart`}
            aria-current={seciliKimlik != null && String(seciliKimlik) === String(kimlik) ? 'true' : undefined}
            onClick={() => onSatirTikla(satir)}
          >
            {govde}
          </button>
        ) : (
          <div className={sinif} data-testid={`${testid}-kart`}>{govde}</div>
        )}
      </li>
    );
  };

  // Tek kalemlik listede sıralama anlamsız (Yerlice: "kullanılmayan UI'yı kaldır").
  const siralamaVar = !yukleniyor && sirali.length > 1 && secenekler.length > 0;

  return (
    <div
      data-testid={testid}
      data-kip="kart"
      data-stil={kartStili}
      data-yenileniyor={yenileniyor || undefined}
      aria-busy={yukleniyor || yenileniyor}
      aria-label={etiket}
      role="group"
      className={`rk-kok ${className}`}
    >
      {(kartAraclari || siralamaVar) && (
        <div className="rk-araclar">
          <div className="rk-araclar-sol">{kartAraclari}</div>
          {siralamaVar && <KartSirala secenekler={secenekler} deger={siralama} onDegis={siralaDegis} />}
        </div>
      )}

      {yukleniyor ? (
        <ul className="rk-liste" aria-hidden="true">
          {Array.from({ length: 6 }, (_, i) => (
            <li key={i}>
              <div className="rk-kart" data-iskelet="true">
                <div className="skeleton h-4 rounded" style={{ width: `${55 + (i % 3) * 12}%` }} />
                <div className="skeleton h-3 rounded" style={{ width: `${35 + (i % 4) * 9}%` }} />
              </div>
            </li>
          ))}
        </ul>
      ) : ogeSayisi === 0 ? (
        <div className="rt-bos" data-testid={`${testid}-bos`}>{bosMesaj}</div>
      ) : (
        <ul className="rk-liste" data-testid={`${testid}-liste`}>
          {ogeler.map(kart)}
          {kalan > 0 && (
            <li>
              <button
                type="button"
                className="rk-daha"
                data-testid={`${testid}-daha`}
                onClick={() => setLimit((l) => l + SAYFA_BOYU)}
              >
                +{new Intl.NumberFormat('tr-TR').format(kalan)} kalem daha göster
              </button>
            </li>
          )}
        </ul>
      )}

      {toplamlar && !yukleniyor && (
        <div className="rk-serit" data-testid={`${testid}-toplam`}>
          <KartGovdesi
            yerlesim={yerlesim}
            deger={(k) => (k.kolon.toplam ? toplamlar[k.sira] : undefined)}
            baslik={etiketMetni}
          />
        </div>
      )}
    </div>
  );
}
